import { ConciergeProductCard } from "@/components/pages/AppPages/Concierge/ConciergeProductCard";
import {
  ProductRewardSearchResult,
  SectionBasedProduct,
} from "@brandclub/common-ui";
import { Skeleton, styled } from "@mui/material";
import StoreCarousel from "./StoreCarousel";

interface ProductGroupCarouselProps {
  products: SectionBasedProduct[];
  allStacklineSkus?: string[];
  isLoading?: boolean;
}

const CarouselContainer = styled("div")({
  width: "100%",
});

const SkeletonCard = styled("div")({
  width: "240px",
  height: "391.86px",
  borderRadius: "12px",
});

export const ProductGroupCarousel: React.FC<ProductGroupCarouselProps> = ({
  products,
  allStacklineSkus = [],
  isLoading = false,
}) => {
  // Show loading skeletons if loading and no products
  if (isLoading && products.length === 0) {
    return (
      <CarouselContainer>
        <StoreCarousel>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                sx={{ borderRadius: "12px" }}
              />
            </SkeletonCard>
          ))}
        </StoreCarousel>
      </CarouselContainer>
    );
  }

  // Don't render if no products and no allStacklineSkus (nothing to show)
  if ((!products || products.length === 0) && allStacklineSkus.length === 0) {
    return null;
  }

  // Create a map of products by stacklineSku for easy lookup
  const productMap = new Map<string, SectionBasedProduct>();
  products.forEach((product) => {
    productMap.set(product.stacklineSku, product);
  });

  // Use allStacklineSkus if provided, otherwise fall back to products
  const skusToRender =
    allStacklineSkus.length > 0
      ? allStacklineSkus
      : products.map((p) => p.stacklineSku);

  return (
    <CarouselContainer className="product-group-carousel">
      <StoreCarousel>
        {skusToRender.map((sku) => {
          const product = productMap.get(sku);

          // If product metadata is available, render the product card
          if (product) {
            return (
              <div
                key={sku}
                style={{
                  width: "240px",
                  minWidth: "240px",
                }}
              >
                <ConciergeProductCard
                  product={product as unknown as ProductRewardSearchResult}
                />
              </div>
            );
          }

          // If metadata not available yet, render skeleton
          return null;
        })}
      </StoreCarousel>
    </CarouselContainer>
  );
};
